import { useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import ConfirmModal from '../components/ConfirmModal';
import SortableTable from '../components/SortableTable';
import useAdminAction from '../hooks/useAdminAction';
import { ADMIN_ACTIONS } from '../constants/adminActions';
import {
  checkPromoCodeExists,
  fetchPromoUsages,
  fetchPromos,
  promoRef,
} from '../services/firestore';

const PAGE_SIZE = 20;

function toDateTime(dateValue) {
  if (!dateValue?.toDate) return '-';
  return dateValue.toDate().toLocaleString();
}

function toDate(dateValue) {
  if (!dateValue?.toDate) return 'No expiry';
  return dateValue.toDate().toLocaleDateString();
}

export default function PromoCodes() {
  const { performAction, loading: actionLoading, error: actionError } = useAdminAction();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedCode, setExpandedCode] = useState('');
  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    code: '',
    discountType: 'percent',
    discountValue: '',
    appliesTo: 'all',
    usageLimit: '',
    validFrom: new Date().toISOString().slice(0, 10),
    validUntil: '',
    note: '',
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const promos = await fetchPromos();
      setRows(promos);
    } catch (err) {
      setError(err?.message || 'Failed to load promo codes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'active' && row.isActive)
        || (statusFilter === 'inactive' && !row.isActive);
      const matchesText = !needle
        || String(row.id || '').toLowerCase().includes(needle)
        || String(row.appliesTo || '').toLowerCase().includes(needle)
        || String(row.note || '').toLowerCase().includes(needle);
      return matchesStatus && matchesText;
    });
  }, [q, rows, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = useMemo(() => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filteredRows, page]);

  useEffect(() => {
    setPage(1);
  }, [q, statusFilter]);

  const onChangeCode = (value) => {
    const normalized = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    setForm((prev) => ({ ...prev, code: normalized }));
  };

  const onCreate = async () => {
    setFormError('');
    const code = form.code.trim().toUpperCase();
    const numericDiscount = Number(form.discountValue);
    const usageLimit = form.usageLimit ? Number(form.usageLimit) : null;

    if (!code) {
      setFormError('Code is required.');
      return;
    }
    if (!form.validFrom) {
      setFormError('Valid from is required.');
      return;
    }
    if (!Number.isFinite(numericDiscount) || numericDiscount <= 0) {
      setFormError('Discount value must be greater than 0.');
      return;
    }
    if (form.discountType === 'percent' && numericDiscount > 100) {
      setFormError('Percent discount must be 100 or below.');
      return;
    }
    if (usageLimit !== null && (!Number.isFinite(usageLimit) || usageLimit <= 0)) {
      setFormError('Usage limit must be positive or left blank.');
      return;
    }

    const exists = await checkPromoCodeExists(code);
    if (exists) {
      setFormError('Promo code already exists.');
      return;
    }

    const payload = {
      code,
      discountType: form.discountType,
      discountValue: numericDiscount,
      appliesTo: form.appliesTo,
      usageLimit,
      usageCount: 0,
      validFrom: Timestamp.fromDate(new Date(`${form.validFrom}T00:00:00`)),
      validUntil: form.validUntil ? Timestamp.fromDate(new Date(`${form.validUntil}T23:59:59`)) : null,
      isActive: true,
      createdBy: auth.currentUser?.email || 'unknown',
      createdAt: Timestamp.now(),
      note: form.note.trim() || null,
    };

    await performAction({
      action: ADMIN_ACTIONS.PROMO_CREATED,
      targetBusinessId: null,
      before: null,
      after: { code, discountType: payload.discountType, discountValue: payload.discountValue },
      writeFn: (batch, batchDb) => {
        batch.set(promoRef(batchDb, code), payload);
      },
    });

    setForm({
      code: '',
      discountType: 'percent',
      discountValue: '',
      appliesTo: 'all',
      usageLimit: '',
      validFrom: new Date().toISOString().slice(0, 10),
      validUntil: '',
      note: '',
    });
    setCreateOpen(false);
    await load();
  };

  const onDeactivate = async () => {
    if (!confirmDeactivate) return;
    await performAction({
      action: ADMIN_ACTIONS.PROMO_DEACTIVATED,
      targetBusinessId: null,
      before: { code: confirmDeactivate.id, isActive: confirmDeactivate.isActive },
      after: { code: confirmDeactivate.id, isActive: false },
      writeFn: (batch, batchDb) => {
        batch.set(promoRef(batchDb, confirmDeactivate.id), { isActive: false }, { merge: true });
      },
    });
    setConfirmDeactivate(null);
    await load();
  };

  const toggleUsage = async (code) => {
    if (expandedCode === code) {
      setExpandedCode('');
      setUsageRows([]);
      return;
    }
    setExpandedCode(code);
    setUsageLoading(true);
    try {
      const usages = await fetchPromoUsages(code);
      setUsageRows(usages);
    } finally {
      setUsageLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'code',
      header: 'Code',
      accessor: (row) => row.id,
      render: (row) => (
        <button type="button" className="wb-link-btn" onClick={() => toggleUsage(row.id)}>
          {row.id}
        </button>
      ),
    },
    { key: 'type', header: 'Type', accessor: 'discountType', render: (row) => row.discountType || '-' },
    { key: 'value', header: 'Value', accessor: 'discountValue', render: (row) => row.discountValue ?? '-' },
    { key: 'appliesTo', header: 'Applies To', accessor: 'appliesTo', render: (row) => row.appliesTo || '-' },
    {
      key: 'usage',
      header: 'Usage',
      accessor: (row) => row.usageCount || 0,
      render: (row) => `${row.usageCount || 0}/${row.usageLimit == null ? 'infinity' : row.usageLimit}`,
    },
    { key: 'validUntil', header: 'Valid Until', accessor: 'validUntil', render: (row) => toDate(row.validUntil) },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => (row.isActive ? 'active' : 'inactive'),
      render: (row) => (
        <span className={`wb-tag ${row.isActive ? 'wb-tag--active' : 'wb-tag--expired'}`}>
          {row.isActive ? 'active' : 'inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      accessor: 'id',
      sortable: false,
      render: (row) => (row.isActive ? (
        <button
          type="button"
          className="danger"
          onClick={() => setConfirmDeactivate(row)}
          disabled={actionLoading}
        >
          Deactivate
        </button>
      ) : null),
    },
  ], [actionLoading]);

  return (
    <>
      <div className="wb-card">
        <div className="wb-row" style={{ justifyContent: 'space-between' }}>
          <h3 className="wb-section-title">Promo Codes</h3>
          <button type="button" onClick={() => setCreateOpen(true)}>Create Promo Code</button>
        </div>
        <div className="wb-toolbar-grid" style={{ marginBottom: 12 }}>
          <input placeholder="Filter by code, plan, or note" value={q} onChange={(event) => setQ(event.target.value)} />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="wb-muted">{filteredRows.length} promo codes</span>
        </div>
        {loading ? <div className="wb-skeleton wb-skeleton--table" /> : null}
        {error ? <p className="wb-error-text">{error}</p> : null}
        {!loading && filteredRows.length === 0 ? <p className="wb-muted">No promo codes available.</p> : null}

        {!loading && filteredRows.length > 0 ? (
          <>
            <SortableTable
              columns={columns}
              rows={pagedRows}
              defaultSort={{ key: 'code', dir: 'asc' }}
              renderExpanded={(row, colSpan) => (expandedCode === row.id ? (
                <tr>
                  <td colSpan={colSpan}>
                    {usageLoading ? <div className="wb-skeleton" /> : null}
                    {!usageLoading && usageRows.length === 0 ? <p className="wb-muted">No usages yet.</p> : null}
                    {!usageLoading && usageRows.length > 0 ? (
                      <table className="wb-table wb-subtable">
                        <thead>
                          <tr>
                            <th>Business</th>
                            <th>Applied At</th>
                            <th>Discount Given</th>
                            <th>Plan At Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usageRows.map((usage) => (
                            <tr key={usage.id}>
                              <td>{usage.businessName} ({usage.businessId || '-'})</td>
                              <td>{toDateTime(usage.appliedAt || usage.usedAt)}</td>
                              <td>{usage.discountGiven ?? '-'}</td>
                              <td>{usage.planAtTime || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : null}
                  </td>
                </tr>
              ) : null)}
            />

            <div className="wb-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Next</button>
            </div>
          </>
        ) : null}
      </div>

      {createOpen ? (
        <div className="wb-drawer-backdrop">
          <div className="wb-drawer">
            <h3>Create Promo Code</h3>
            <input
              placeholder="Code"
              value={form.code}
              onChange={(event) => onChangeCode(event.target.value)}
            />
            <div className="wb-row">
              <label><input type="radio" checked={form.discountType === 'percent'} onChange={() => setForm((prev) => ({ ...prev, discountType: 'percent' }))} /> Percent</label>
              <label><input type="radio" checked={form.discountType === 'flat'} onChange={() => setForm((prev) => ({ ...prev, discountType: 'flat' }))} /> Flat</label>
            </div>
            <input
              type="number"
              min="1"
              placeholder="Discount value"
              value={form.discountValue}
              onChange={(event) => setForm((prev) => ({ ...prev, discountValue: event.target.value }))}
            />
            <select value={form.appliesTo} onChange={(event) => setForm((prev) => ({ ...prev, appliesTo: event.target.value }))}>
              <option value="all">All plans</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="pro">Pro</option>
            </select>
            <input
              type="number"
              min="1"
              placeholder="Usage limit (optional)"
              value={form.usageLimit}
              onChange={(event) => setForm((prev) => ({ ...prev, usageLimit: event.target.value }))}
            />
            <label>
              Valid from
              <input
                type="date"
                value={form.validFrom}
                onChange={(event) => setForm((prev) => ({ ...prev, validFrom: event.target.value }))}
              />
            </label>
            <label>
              Valid until
              <input
                type="date"
                value={form.validUntil}
                onChange={(event) => setForm((prev) => ({ ...prev, validUntil: event.target.value }))}
              />
            </label>
            <textarea
              rows={4}
              placeholder="Note (optional)"
              value={form.note}
              onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
            />
            {formError ? <p className="wb-error-text">{formError}</p> : null}
            {actionError ? <p className="wb-error-text">{actionError}</p> : null}
            <div className="wb-modal-actions">
              <button type="button" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button type="button" disabled={actionLoading} onClick={onCreate}>Create</button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(confirmDeactivate)}
        title={`Deactivate ${confirmDeactivate?.id || ''}?`}
        description={`Deactivate ${confirmDeactivate?.id || ''}? Merchants who have this code won't be able to use it.`}
        risk="high"
        onCancel={() => setConfirmDeactivate(null)}
        onConfirm={onDeactivate}
      />
    </>
  );
}
